import React, { useState, useEffect } from 'react';
import { SecurityMonitor } from '../services/securityMonitor';
import { AuditLogger, AuditLog } from '../services/auditLogger';
import { RateLimitService } from '../services/rateLimitService';
import './SecurityDashboard.css';

interface SecurityDashboardProps {
  currentUser: any;
  onClose: () => void;
}

interface SecurityStats {
  totalAlerts: number;
  criticalAlerts: number;
  highAlerts: number;
  mediumAlerts: number;
  lowAlerts: number;
  recentLoginFailures: number;
  recentLoginSuccesses?: number;
  activeSessions: number;
  totalUsers: number;
  securityScore: number;
  failureDetails?: Array<{
    id: string;
    email: string;
    ipAddress: string;
    timestamp: Date;
    userAgent: string;
  }>;
  successDetails?: Array<{
    id: string;
    email: string;
    ipAddress: string;
    timestamp: Date;
    userAgent: string;
    userId: string;
  }>;
}

const SecurityDashboard: React.FC<SecurityDashboardProps> = ({ currentUser, onClose }) => {
  const [stats, setStats] = useState<SecurityStats>({
    totalAlerts: 0,
    criticalAlerts: 0,
    highAlerts: 0,
    mediumAlerts: 0,
    lowAlerts: 0,
    recentLoginFailures: 0,
    activeSessions: 0,
    totalUsers: 0,
    securityScore: 100
  });
  
  const [recentAlerts, setRecentAlerts] = useState<any[]>([]);
  const [recentLogs, setRecentLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    console.log('🔒 Security Dashboard useEffect triggered');
    // Initialize Security Monitor to load stored data
    SecurityMonitor.initialize();
    console.log('🔒 Security Monitor initialized in dashboard');
    loadSecurityData();
    
    if (autoRefresh) {
      const interval = setInterval(loadSecurityData, 30000); // Refresh every 30 seconds
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  const loadSecurityData = async () => {
    try {
      setLoading(true);
      
      // Check localStorage directly
      const directStorageCheck = localStorage.getItem('security_monitor_data');
      console.log('🔒 Direct localStorage check:', directStorageCheck);
      
            // Load security statistics
            const securityStats = await SecurityMonitor.getSecurityStats();
            console.log('🔒 Security Stats:', securityStats);
      
      // Load recent alerts
      const alerts = await SecurityMonitor.getRecentAlerts(20);
      console.log('🔒 Recent Alerts:', alerts);
      
      // Load recent audit logs
      const logs = await AuditLogger.getRecentLogs(50);
      
      // Calculate security score
      const securityScore = calculateSecurityScore(securityStats, alerts);
      
      setStats({
        ...securityStats,
        activeSessions: 1, // Placeholder - would need session tracking
        totalUsers: 1, // Placeholder - would need user count
        securityScore
      });
      
      setRecentAlerts(alerts);
      setRecentLogs(logs);
      
    } catch (error) {
      console.error('Error loading security data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateSecurityScore = (stats: any, alerts: any[]): number => {
    let score = 100;
    
    // Deduct points for alerts
    score -= stats.criticalAlerts * 20;
    score -= stats.highAlerts * 10;
    score -= stats.mediumAlerts * 5;
    score -= stats.lowAlerts * 2;
    
    // Deduct points for recent login failures
    score -= Math.min(stats.recentLoginFailures * 3, 30);
    
    return Math.max(score, 0);
  };

  const getSecurityScoreColor = (score: number): string => {
    if (score >= 80) return '#27ae60'; // Green
    if (score >= 60) return '#f39c12'; // Orange
    if (score >= 40) return '#e67e22'; // Dark orange
    return '#e74c3c'; // Red
  };

  const getSeverityColor = (severity: string): string => {
    switch (severity) {
      case 'CRITICAL': return '#e74c3c';
      case 'HIGH': return '#e67e22';
      case 'MEDIUM': return '#f39c12';
      case 'LOW': return '#27ae60';
      default: return '#95a5a6';
    }
  };

  const formatTimestamp = (timestamp: any): string => {
    if (!timestamp) return 'Unknown';
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleString();
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const truncateUserAgent = (userAgent: string): string => {
    if (!userAgent || userAgent === 'unknown') return 'Unknown';
    
    // Extract browser and OS from user agent string
    const match = userAgent.match(/(Chrome|Firefox|Safari|Edge|Opera)\/[\d.]+|Windows|Mac|Linux|Android|iOS/gi);
    if (match && match.length > 0) {
      return match.slice(0, 2).join(' ');
    }
    
    // If no match, truncate to 40 characters
    return userAgent.length > 40 ? userAgent.substring(0, 40) + '...' : userAgent;
  };

  const clearAllAlerts = () => {
    if (window.confirm('Are you sure you want to clear all security alerts?')) {
      SecurityMonitor.clearAlerts();
      loadSecurityData();
    }
  };

  if (loading) {
    return (
      <div className="security-dashboard">
        <div className="loading">Loading security data...</div>
      </div>
    );
  }

  return (
    <div className="security-dashboard">
      <div className="dashboard-header">
        <h1>🔒 Security Dashboard</h1>
        <div className="header-controls">
          <label className="auto-refresh-toggle">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto Refresh (30s)
          </label>
          <button className="btn-refresh" onClick={loadSecurityData}>
            🔄 Refresh
          </button>
          <button className="btn-close" onClick={onClose}>
            ×
          </button>
        </div>
      </div>

      {/* Security Score */}
      <div className="security-score-section">
        <div className="security-score-card">
          <h2>Security Score</h2>
          <div 
            className="security-score-value"
            style={{ color: getSecurityScoreColor(stats.securityScore) }}
          >
            {stats.securityScore}/100
          </div>
          <div className="security-score-bar">
            <div 
              className="security-score-fill"
              style={{ 
                width: `${stats.securityScore}%`,
                backgroundColor: getSecurityScoreColor(stats.securityScore)
              }}
            />
          </div>
        </div>
      </div>

      {/* Security Statistics */}
      <div className="security-stats">
        <div className="stat-card critical">
          <div className="stat-icon">🚨</div>
          <div className="stat-content">
            <div className="stat-value">{stats.criticalAlerts}</div>
            <div className="stat-label">Critical Alerts</div>
          </div>
        </div>
        
        <div className="stat-card high">
          <div className="stat-icon">⚠️</div>
          <div className="stat-content">
            <div className="stat-value">{stats.highAlerts}</div>
            <div className="stat-label">High Alerts</div>
          </div>
        </div>
        
        <div className="stat-card medium">
          <div className="stat-icon">📊</div>
          <div className="stat-content">
            <div className="stat-value">{stats.mediumAlerts}</div>
            <div className="stat-label">Medium Alerts</div>
          </div>
        </div>
        
        <div className="stat-card low">
          <div className="stat-icon">ℹ️</div>
          <div className="stat-content">
            <div className="stat-value">{stats.lowAlerts}</div>
            <div className="stat-label">Low Alerts</div>
          </div>
        </div>
        
        <div className="stat-card login-failures">
          <div className="stat-icon">🔐</div>
          <div className="stat-content">
            <div className="stat-value">{stats.recentLoginFailures}</div>
            <div className="stat-label">Login Failures</div>
          </div>
        </div>
        
        <div className="stat-card login-successes">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <div className="stat-value">{stats.recentLoginSuccesses || 0}</div>
            <div className="stat-label">Login Successes</div>
          </div>
        </div>
        
        <div className="stat-card total-alerts">
          <div className="stat-icon">📈</div>
          <div className="stat-content">
            <div className="stat-value">{stats.totalAlerts}</div>
            <div className="stat-label">Total Alerts</div>
          </div>
        </div>
      </div>

      {/* Recent Successful Logins */}
      {stats.successDetails && stats.successDetails.length > 0 && (
        <div className="login-successes-section">
          <div className="section-header">
            <h2>✅ Recent Successful Logins (Last 24h)</h2>
          </div>
          
          <div className="successes-table">
            <table>
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Email</th>
                  <th>IP Address</th>
                  <th>User Agent</th>
                </tr>
              </thead>
              <tbody>
                {stats.successDetails.map((success) => (
                  <tr key={success.id}>
                    <td>{formatTimestamp(success.timestamp)}</td>
                    <td className="success-email">{success.email}</td>
                    <td className="success-ip">🌐 {success.ipAddress}</td>
                    <td className="success-agent">{truncateUserAgent(success.userAgent)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent Login Failures */}
      {stats.failureDetails && stats.failureDetails.length > 0 && (
        <div className="login-failures-section">
          <div className="section-header">
            <h2>🔐 Recent Login Failures (Last 24h)</h2>
          </div>
          
          <div className="failures-table">
            <table>
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Email</th>
                  <th>IP Address</th>
                  <th>User Agent</th>
                </tr>
              </thead>
              <tbody>
                {stats.failureDetails.map((failure) => (
                  <tr key={failure.id}>
                    <td>{formatTimestamp(failure.timestamp)}</td>
                    <td className="failure-email">{failure.email}</td>
                    <td className="failure-ip">🌐 {failure.ipAddress}</td>
                    <td className="failure-agent">{truncateUserAgent(failure.userAgent)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent Security Alerts */}
      <div className="security-alerts-section">
        <div className="section-header">
          <h2>🚨 Recent Security Alerts</h2>
          <button className="btn-clear-alerts" onClick={clearAllAlerts}>
            Clear All Alerts
          </button>
        </div>
        
        <div className="alerts-list">
          {recentAlerts.length === 0 ? (
            <div className="no-alerts">✅ No security alerts</div>
          ) : (
            recentAlerts.map((alert, index) => (
              <div key={alert.id} className="alert-item">
                <div className="alert-severity" style={{ backgroundColor: getSeverityColor(alert.severity) }}>
                  {alert.severity}
                </div>
                <div className="alert-content">
                  <div className="alert-message">{alert.message}</div>
                  <div className="alert-details">
                    <span className="alert-time">{formatTimestamp(alert.timestamp)}</span>
                    {alert.userEmail && (
                      <span className="alert-user">User: {alert.userEmail}</span>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Recent Audit Logs */}
      <div className="audit-logs-section">
        <div className="section-header">
          <h2>📋 Recent Audit Logs</h2>
        </div>
        
        <div className="logs-list">
          {recentLogs.length === 0 ? (
            <div className="no-logs">No audit logs available</div>
          ) : (
            recentLogs.map((log, index) => (
              <div key={log.id || index} className="log-item">
                <div className="log-severity" style={{ backgroundColor: getSeverityColor(log.severity) }}>
                  {log.severity}
                </div>
                <div className="log-content">
                  <div className="log-action">{log.action}</div>
                  <div className="log-details">
                    <span className="log-time">{formatTimestamp(log.timestamp)}</span>
                    <span className="log-user">User: {log.userEmail}</span>
                    <span className="log-resource">Resource: {log.resource}</span>
                  </div>
                  {log.success === false && (
                    <div className="log-error">Error: {log.errorMessage}</div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default SecurityDashboard;

